import { useAppDispatch, useAppSelector } from '@/hooks';
import { useAuth } from '@/hooks/useAuth';
import { MenuModel } from '@/models';
import { MenuRole } from '@/models/role';
import { HRRole } from '@/modules/hr-management/models';
import { menuActions, selectMenuList } from '@/modules/menu/core/menuSlice';
import { getRoleEmployee } from '@/roles';
import { trans } from '@/utils';

// Spec §4.1 + §RB — "Sidebar chỉ hiện menu user có quyền".
// OWNER/ADMIN bypass through getRoleEmployee (returns true for non-MEMBER);
// MEMBER must own the matching permission code from authoriz.json. The QC
// bug was that the five "legacy" menu groups (Kế hoạch TD, Tổ chức TD,
// Khách hàng, NLĐ, Nhân sự) were rendered unconditionally — so a MEMBER
// who'd been granted only KHTD_* still saw the entire app.
function useMenu() {
  const dispatch = useAppDispatch();
  const menuList = useAppSelector(selectMenuList);
  const { currentUser } = useAuth();

  // Recruitment domain
  const khtdXem = getRoleEmployee(MenuRole.khtd_xem);
  const tctdXem = getRoleEmployee(MenuRole.tctd_xem);
  const vhnldQL = getRoleEmployee(MenuRole.vhnld_ql);

  // Communication sub-menu
  const qlttHT = getRoleEmployee(MenuRole.qltt_ht);
  const qlttKSL = getRoleEmployee(MenuRole.qltt_ksl);
  const qlttKSTD = getRoleEmployee(MenuRole.qltt_kstd);

  // Core lists
  const qlkhXem = getRoleEmployee(MenuRole.qlkh_xem);
  const qlnldXem = getRoleEmployee(MenuRole.qlnld_xem);

  // HR sub-menu (3 items, each its own permission)
  const qltkAdminXem = getRoleEmployee(MenuRole.qltkadmin_xem);
  const qltkMemXem = getRoleEmployee(MenuRole.qltkmem_xem);
  const qlqXem = getRoleEmployee(MenuRole.qlq_xem);
  const qlnXem = getRoleEmployee(MenuRole.qln_xem);
  const hrListVisible = qltkAdminXem || qltkMemXem; // shared list page

  // V1.4 modules
  const qlnmXem = getRoleEmployee(MenuRole.qlnm_xem);
  const dutQl = getRoleEmployee(MenuRole.dut_ql);
  const vphmXem = getRoleEmployee(MenuRole.vphm_xem);
  const npnvXem = getRoleEmployee(MenuRole.npnv_xem);
  const tcktXem = getRoleEmployee(MenuRole.tckt_xem);
  const tbGui = getRoleEmployee(MenuRole.tb_gui);
  const vlXem = getRoleEmployee(MenuRole.vl_xem);
  const rpXem = getRoleEmployee(MenuRole.rp_xem);
  const cfgXem = getRoleEmployee(MenuRole.cfg_xem);

  const QLNhaMay = currentUser?.account_type === HRRole.factory;

  // Build HR sub-children — render the parent only if at least one child is
  // visible (otherwise the empty group would still appear in the sidebar).
  const hrChildren: MenuModel[] = [];
  if (hrListVisible) {
    hrChildren.push({
      hasBullet: true,
      title: 'Quản lý tài khoản',
      to: '/hr-management/management/list',
    });
  }
  if (qlqXem) {
    hrChildren.push({
      hasBullet: true,
      title: 'Danh sách quyền',
      to: '/hr-management/role-list',
    });
  }
  if (qlnXem) {
    hrChildren.push({
      hasBullet: true,
      title: trans('MENU.HR_MANAGEMENT.LIST_OF_RECRUITMENT_TEAMS'),
      to: '/hr-management/recruitment-teams',
    });
  }

  // Communication parent visible only if at least one child is.
  const commChildren: MenuModel[] = [];
  if (qlttKSTD) {
    commChildren.push({
      hasBullet: true,
      title: 'Tổ chức khảo sát tự do NLĐ',
      to: '/communication/free-survey/list',
    });
  }
  if (qlttKSL) {
    commChildren.push({
      hasBullet: true,
      title: 'Tổ chức khảo sát lương NLĐ',
      to: '/communication/salary-survey/list',
    });
  }
  if (qlttHT) {
    commChildren.push({
      hasBullet: true,
      title: 'Hỗ trợ NLĐ',
      to: '/communication/worker-support',
    });
  }

  const groupChildren: MenuModel[] = [];

  if (khtdXem) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/recruitment_plan.svg',
      title: 'Kế hoạch tuyển dụng',
      to: '/recruitment-plan/list',
    });
  }
  if (tctdXem) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/recruitment_organization.svg',
      title: 'Tổ chức tuyển dụng',
      to: '/recruitment-organization/list',
    });
  }
  if (vhnldQL) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/labor_operation.svg',
      title: 'Vận hành lao động',
      to: '/operate-worker/list',
    });
  }
  if (commChildren.length > 0) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      children: commChildren,
      icon: '/media/icons/duotune/menu/communication.svg',
      title: 'Quản lý truyền thông',
      to: '/communication',
    });
  }
  if (qlkhXem) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/customer.svg',
      title: 'Quản lý khách hàng',
      to: '/customer/list',
    });
  }
  if (qlnldXem) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/workers.svg',
      title: 'Quản lý NLĐ',
      to: '/worker/list',
    });
  }

  if (hrChildren.length > 0) {
    groupChildren.push({
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      children: hrChildren,
      icon: '/media/icons/duotune/menu/human_resources_management.svg',
      menu: {
        hasArrow: true,
        menuPlacement: 'right-start',
        menuTrigger: `{default:'click', lg: 'hover'}`,
      },
      title: trans('MENU.HR_MANAGEMENT'),
      to: '/hr-management',
    });
  }

  // V1.4 modules — each one its own top-level entry.
  // if (qlnmXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-building', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/customer.svg',
  //     title: 'Nhà máy (V1.4 A1)',
  //     to: '/factory/list',
  //   });
  // }
  // if (dutQl) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-clipboard-data', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/customer.svg',
  //     title: 'Đơn ứng tuyển từ App',
  //     to: '/application',
  //   });
  // }
  // if (vphmXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-flag', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/workers.svg',
  //     title: 'Lỗi vi phạm',
  //     to: '/violation',
  //   });
  // }
  // if (npnvXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-calendar-x', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/labor_operation.svg',
  //     title: 'Duyệt nghỉ phép',
  //     to: '/leave-request',
  //   });
  // }
  // if (tcktXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-cash-stack', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/labor_operation.svg',
  //     title: 'Tài chính (Bảng lương / Quyết toán)',
  //     to: '/payroll',
  //   });
  // }
  // if (tbGui) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-megaphone', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/communication.svg',
  //     title: 'Đẩy thông báo',
  //     to: '/notification',
  //   });
  // }
  // if (vlXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-briefcase', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/communication.svg',
  //     title: 'Thông tin việc làm',
  //     to: '/job-post',
  //   });
  // }
  // if (rpXem) {
  //   groupChildren.push({
  //     aside: { fontIcon: 'bi-bar-chart-line', icon: '/media/icons/duotune/art/art002.svg' },
  //     icon: '/media/icons/duotune/menu/recruitment_plan.svg',
  //     title: 'Báo cáo (17 báo cáo)',
  //     to: '/report',
  //   });
  // }
  if (cfgXem) {
    groupChildren.push({
      aside: { fontIcon: 'bi-gear', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/human_resources_management.svg',
      title: 'Cấu hình tham số',
      to: '/system-parameter',
    });
  }

  // Dashboard is shown to everyone signed in — it's the landing page, with
  // its own per-card permission gating inside the page itself.
  const defaultMenuList: MenuModel[] = [
    {
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/general/ic_dashboad.svg',
      title: trans('MENU.DASHBOARD'),
      to: '/dashboard',
    },
    ...groupChildren,
  ];

  const factoryMenuList: MenuModel[] = [
    {
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/recruitment_plan.svg',
      title: 'Kế hoạch tuyển dụng',
      to: '/recruitment-plan/list',
    },
    {
      aside: { fontIcon: 'bi-app-indicator', icon: '/media/icons/duotune/art/art002.svg' },
      icon: '/media/icons/duotune/menu/labor_operation.svg',
      title: 'Vận hành lao động',
      to: '/operate-worker/list',
    },
  ];

  function setMenuList(menuList: MenuModel[]) {
    dispatch(menuActions.setMenuList(menuList));
  }

  function setDefaultMenuList() {
    if (QLNhaMay) {
      setMenuList(factoryMenuList);
    } else {
      setMenuList(defaultMenuList);
    }
  }

  return { menuList, setDefaultMenuList, setMenuList };
}

export { useMenu };
